/**
 * Freedom Sale Pass — campaign state machine and countdown.
 *
 * The section root carries data-fs-start / data-fs-end as epoch milliseconds
 * and data-fs-state as the server-rendered state. This file re-resolves the
 * state on load (the served HTML may have been cached across a boundary),
 * ticks the countdown, and hides the app form section once sign-ups close.
 *
 * CSS does all showing and hiding, driven solely by data-fs-state on the root.
 */
(function () {
  'use strict';

  /* The section emits its own <script src> tag, so a page carrying two
     Freedom Sale Pass sections executes this file twice. A second execution
     would build a second, empty `timers` registry and overwrite the exported
     API with functions closed over it — the surviving stop() could no longer
     reach an interval the earlier run had started, so every section re-render
     would leak a ticking timer. One module instance per page, always. */
  if (window.theme && window.theme.freedomSalePass) return;

  window.theme = window.theme || {};

  var TICK_MS = 1000;

  /* Module-scoped registry of running interval ids, keyed by the section
     root's element id (not by node identity). Shopify's Section Rendering
     API replaces a section's markup wholesale on shopify:section:load, so
     the DOM node init() sees the second time round is a brand-new object
     with no memory of the old one — only its id (server-rendered
     identically across the re-render) survives to key off. stop() looks a
     timer up by that id and clears it before start() records a new one
     under the same key, so a stale interval never keeps ticking against a
     detached node. A root with no id cannot be deduped this way. */
  var timers = {};

  function timerKey(root) {
    return root.id || null;
  }

  /* Pure. Which state is the campaign in at time `now`?
     Boundaries are inclusive of the sale window on both ends. */
  function resolveState(now, start, end) {
    if (now < start) return 'pre';
    if (now <= end) return 'live';
    return 'over';
  }

  /* Pure. Milliseconds remaining -> whole units, never negative. */
  function breakdown(ms) {
    if (!(ms > 0)) ms = 0;
    var total = Math.floor(ms / 1000);
    return {
      days: Math.floor(total / 86400),
      hours: Math.floor((total % 86400) / 3600),
      minutes: Math.floor((total % 3600) / 60),
      seconds: total % 60
    };
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function num(el, name) {
    var v = parseInt(el.getAttribute(name), 10);
    return isNaN(v) ? 0 : v;
  }

  function setUnit(root, unit, value) {
    var el = root.querySelector('[data-fs-unit="' + unit + '"]');
    if (el) el.textContent = pad(value);
  }

  /* Stamp the state, fill the countdown, and show or hide the app form.
     Safe to call repeatedly — every branch sets both sides of the toggle,
     so returning to an earlier state fully restores it. */
  function apply(root, now) {
    var start = num(root, 'data-fs-start');
    var end = num(root, 'data-fs-end');
    var state = resolveState(now, start, end);

    root.setAttribute('data-fs-state', state);

    var target = state === 'pre' ? start : end;
    var parts = breakdown(target - now);
    setUnit(root, 'days', parts.days);
    setUnit(root, 'hours', parts.hours);
    setUnit(root, 'minutes', parts.minutes);
    setUnit(root, 'seconds', parts.seconds);

    var formId = root.getAttribute('data-fs-form-id');
    if (formId) {
      var form = document.getElementById(formId);
      if (form) {
        var hide = state !== 'pre';
        /* Two independent mechanisms, deliberately. The [hidden] attribute
           only hides the form because assets/freedom-sale-pass.css restores
           it against theme.css's `[role="main"] .shopify-section:not(.contents)
           { display: flow-root }`, which otherwise outranks `[hidden]`. The
           inline style does not depend on that cascade surviving a theme
           update — and sign-ups staying open after the sale opens is the one
           failure this section must not have. */
        form.hidden = hide;
        form.style.display = hide ? 'none' : '';
      }
    }

    return state;
  }

  /* Clear the running interval for this section, if any. Keyed by id so it
     also reaches a timer left behind by a now-detached predecessor node
     that shared the same server-rendered id (see `timers` above). */
  function stop(root) {
    var key = timerKey(root);
    if (key && timers[key]) {
      clearInterval(timers[key]);
      delete timers[key];
    }
  }

  function start(root) {
    stop(root);

    apply(root, Date.now());

    var timerId = setInterval(function () {
      apply(root, Date.now());
    }, TICK_MS);

    var key = timerKey(root);
    if (key) timers[key] = timerId;
  }

  /* If two or more Freedom Sale Pass sections are bound at once — the
     countdown/body split renders exactly this, one instance above the app
     form and one below it — each resolves its own campaign window
     server-side from its own settings. A merchant edit that touches only
     one instance's Sale starts / Sale ends would then leave the two
     silently disagreeing: the countdown ticking to one date while the CTA
     and terms speak to another. This file is not the source of truth for
     the dates (the template is), so it does not try to reconcile them —
     it only warns once, naming every section id, so the drift gets
     noticed instead of shipped quietly. */
  function warnOnDateMismatch(roots) {
    if (roots.length < 2) return;

    var start = roots[0].getAttribute('data-fs-start');
    var end = roots[0].getAttribute('data-fs-end');
    var mismatched = false;
    var i;

    for (i = 1; i < roots.length; i++) {
      if (roots[i].getAttribute('data-fs-start') !== start || roots[i].getAttribute('data-fs-end') !== end) {
        mismatched = true;
        break;
      }
    }

    if (!mismatched) return;

    var ids = [];
    for (i = 0; i < roots.length; i++) {
      ids.push(roots[i].id || '(no id)');
    }
    console.warn('Freedom Sale Pass: campaign dates disagree across sections — ' + ids.join(', '));
  }

  function init() {
    var roots = document.querySelectorAll('.fs-section[data-fs-start]');
    Array.prototype.forEach.call(roots, start);
    warnOnDateMismatch(Array.prototype.slice.call(roots));
  }

  window.theme.freedomSalePass = {
    resolveState: resolveState,
    breakdown: breakdown,
    pad: pad,
    apply: apply,
    stop: stop
  };

  init();

  // The Section Rendering API does not fire DOMContentLoaded, so without this
  // the theme editor would leave a re-rendered section without its timer.
  document.addEventListener('shopify:section:load', function () {
    init();
  });
})();
